import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiXCircle,
  FiActivity,
  FiZap,
  FiDroplet,
  FiPower,
  FiEdit2
} from "react-icons/fi";
import { MdSettingsInputAntenna, MdRouter } from "react-icons/md";
import { IotmateButton, DialogBox, StatCard, SectionHeader, InfoCard } from "../../components/Shared";
import { Table } from "../../components/Table";
import fetchDataFromApi from "../../api/IotmateRestApi";
import { generateDummyControlUnitDetails, generateDummyControlUnitHistory } from "../../Data/Data";
import "./ControlUnitStpDetails.css";

const ControlUnitStpDetails = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [controlUnitData, setControlUnitData] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    let isInitialLoad = true;

    const fetchData = async () => {
      try {
        if (isInitialLoad) {
          setLoading(true);
        }
        const data = await fetchDataFromApi(`control_unit_details/${deviceId}`);
        setControlUnitData(data || generateDummyControlUnitDetails(deviceId));
      } catch (error) {
        console.error("Error fetching control unit details:", error);
        setControlUnitData(generateDummyControlUnitDetails(deviceId));
      } finally {
        if (isInitialLoad) {
          setLoading(false);
          isInitialLoad = false;
        }
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [deviceId]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const data = await fetchDataFromApi(`control_unit_history/${deviceId}`);
        setHistoryData(data || generateDummyControlUnitHistory(deviceId));
      } catch (error) {
        console.error("Error fetching control unit history:", error);
        setHistoryData(generateDummyControlUnitHistory(deviceId));
      }
    };

    fetchHistory();
  }, [deviceId]);

  const handleEditClick = () => {
    setNewName(controlUnitData?.name || "");
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setNewName("");
  };

  const handleSaveName = () => {
    if (newName.trim()) {
      setControlUnitData({ ...controlUnitData, name: newName.trim() });
      setIsDialogOpen(false);
      setNewName("");
    }
  };

  const handleMotorToggle = (motorId) => {
    // Toggle motor status
    const updatedMotors = controlUnitData.motors.map(motor => {
      if (motor.id === motorId) {
        return {
          ...motor,
          status: motor.status === "Running" ? "Off" : "Running"
        };
      }
      return motor;
    });
    setControlUnitData({ ...controlUnitData, motors: updatedMotors });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Time",
        accessor: "timestamp",
        Cell: ({ value }) => (
          <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
            {value}
          </span>
        ),
      },
      {
        Header: "Event Type",
        accessor: "eventType",
        Cell: ({ row, value }) => (
          <span style={{ fontWeight: '600', color: '#374151' }}>
            {row.original.icon} {value}
          </span>
        ),
      },
      {
        Header: "Message",
        accessor: "message",
        Cell: ({ value }) => (
          <span style={{ color: '#374151', fontSize: '0.875rem' }}>
            {value}
          </span>
        ),
      },
      {
        Header: "LoRa RX (dBm)",
        accessor: "loraRx",
        Cell: ({ value }) => (
          <span style={{
            color: value > -75 ? '#10b981' : value > -85 ? '#f59e0b' : '#ef4444',
            fontWeight: '600'
          }}>
            {value}
          </span>
        ),
      },
      {
        Header: "LoRa TX (dBm)",
        accessor: "loraTx",
        Cell: ({ value }) => (
          <span style={{
            color: value > -75 ? '#10b981' : value > -85 ? '#f59e0b' : '#ef4444',
            fontWeight: '600'
          }}>
            {value}
          </span>
        ),
      },
    ],
    []
  );

  if (loading) {
    return (
      <div className="control-unit-loading">
        <div className="spinner"></div>
        <p>Loading control unit details...</p>
      </div>
    );
  }

  if (!controlUnitData) {
    return (
      <div className="control-unit-error">
        <p>Failed to load control unit data</p>
        <button onClick={() => navigate(-1)} className="control-unit-back-btn">
          Back
        </button>
      </div>
    );
  }

  const tanksRunning = controlUnitData.tanks.filter(t => t.status !== "Critical").length;
  const motorsRunning = controlUnitData.motors.filter(m => m.status === "Running").length;

  return (
    <div className="control-unit-container">
      {/* Header */}
      <div className="control-unit-header">
        <button onClick={() => navigate(-1)} className="control-unit-back-btn">
          <FiArrowLeft size={20} />
          Back
        </button>
        <div className="control-unit-title-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1 className="control-unit-title">
              {controlUnitData.name || `Control Unit ${deviceId}`}
            </h1>
            <IotmateButton onClick={handleEditClick} variant="default" className="iotmate-btn-icon">
              <FiEdit2 size={18} />
            </IotmateButton>
          </div>
          <div className="control-unit-status">
            {controlUnitData.status === "Online" ? (
              <FiCheckCircle size={24} color="#10b981" />
            ) : (
              <FiXCircle size={24} color="#ef4444" />
            )}
            <span
              style={{
                color: controlUnitData.status === "Online" ? "#10b981" : "#ef4444",
              }}
            >
              {controlUnitData.status || "Offline"}
            </span>
          </div>
        </div>
        <p className="control-unit-last-update">
          Last updated: {controlUnitData.lastUpdate || "N/A"}
        </p>
      </div>

      {/* Statistics */}
      <div className="control-unit-stats">
        <StatCard
          value={controlUnitData.tanks.length}
          label="Total Tanks"
          variant="total"
        />
        <StatCard
          value={tanksRunning}
          label="Tanks OK"
          variant="online"
        />
        <StatCard
          value={controlUnitData.motors.length}
          label="Total Motors"
          variant="connectivity"
        />
        <StatCard
          value={motorsRunning}
          label="Motors Running"
          variant="online"
        />
      </div>

      {/* Main Info Cards */}
      <div className="info-card-grid">
        <InfoCard
          icon={<MdRouter />}
          iconColor="#667eea"
          title="Device ID"
          value={controlUnitData.deviceId}
        />
        <InfoCard
          icon={<FiActivity />}
          iconColor="#8b5cf6"
          title="Version"
          value={controlUnitData.version}
        />
        <InfoCard
          icon={<MdSettingsInputAntenna />}
          iconColor="#10b981"
          title="LoRa Gateway"
          value={controlUnitData.loraGateway}
          subtitle={`RX: ${controlUnitData.loraRx} dBm | TX: ${controlUnitData.loraTx} dBm`}
        />
        <InfoCard
          icon={<FiZap />}
          iconColor="#f59e0b"
          title="Battery"
          value={`${controlUnitData.battery}%`}
          subtitle={`Uptime: ${controlUnitData.uptime}`}
        />
      </div>

      {/* Water Tanks Section */}
      <div className="control-unit-section">
        <SectionHeader title="Water Tank Levels" />
        <div className="control-unit-tanks-grid">
          {controlUnitData.tanks.map((tank) => (
            <div key={tank.id} className="tank-card">
              <div className="tank-header">
                <div className="tank-icon">
                  <FiDroplet size={24} color="#3b82f6" />
                </div>
                <div className="tank-info">
                  <h3 className="tank-name">{tank.name}</h3>
                  <span className={`tank-status status-${tank.status.toLowerCase()}`}>
                    {tank.status}
                  </span>
                </div>
              </div>

              <div className="tank-level">
                <div className="tank-level-value">
                  <span className="level-number">{tank.currentLevel}</span>
                  <span className="level-unit">%</span>
                </div>
                <div className="tank-capacity">
                  {Math.floor((tank.currentLevel / 100) * tank.capacity)} / {tank.capacity} {tank.unit}
                </div>
              </div>

              <div className="tank-progress">
                <div
                  className="tank-progress-bar"
                  style={{
                    width: `${tank.currentLevel}%`,
                    background: tank.currentLevel > 70 ? '#10b981' : tank.currentLevel > 30 ? '#f59e0b' : '#ef4444'
                  }}
                />
              </div>

              <div className="tank-footer">
                <span className="tank-footer-label">Last Filled:</span>
                <span className="tank-footer-value">{tank.lastFilled}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Motors Section */}
      <div className="control-unit-section">
        <SectionHeader title="Motor Control Panel" />
        <div className="control-unit-motors-grid">
          {controlUnitData.motors.map((motor) => (
            <div key={motor.id} className={`motor-card ${motor.status === "Running" ? "motor-running" : ""}`}>
              <div className="motor-header">
                <div className="motor-icon">
                  <FiPower size={24} color={motor.status === "Running" ? "#10b981" : "#6b7280"} />
                </div>
                <div className="motor-info">
                  <h3 className="motor-name">{motor.name}</h3>
                  <span className="motor-power">{motor.power}</span>
                </div>
              </div>

              <div className="motor-status-badge">
                <span className={`motor-status ${motor.status === "Running" ? "status-running" : "status-off"}`}>
                  {motor.status === "Running" ? "● Running" : "○ Off"}
                </span>
              </div>

              <div className="motor-details">
                <div className="motor-detail-item">
                  <span className="motor-detail-label">Current:</span>
                  <span className="motor-detail-value">{motor.current} A</span>
                </div>
                <div className="motor-detail-item">
                  <span className="motor-detail-label">Voltage:</span>
                  <span className="motor-detail-value">{motor.voltage} V</span>
                </div>
                <div className="motor-detail-item">
                  <span className="motor-detail-label">Runtime:</span>
                  <span className="motor-detail-value">{motor.runtime}</span>
                </div>
              </div>

              <div className="motor-footer">
                <span className="motor-footer-text">
                  Last: {motor.lastStarted}
                </span>
                <IotmateButton
                  onClick={() => handleMotorToggle(motor.id)}
                  variant={motor.status === "Running" ? "danger" : "primary"}
                  size="small"
                >
                  {motor.status === "Running" ? "Stop" : "Start"}
                </IotmateButton>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* History Table */}
      <div className="control-unit-history">
        <Table
          columns={columns}
          data={historyData}
          heading="Event History & LoRa Signal Log"
          typeComplex={true}
        />
      </div>

      {/* Edit Name Dialog */}
      <DialogBox
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        title="Edit Control Unit Name"
        onSave={handleSaveName}
        saveLabel="Save"
        cancelLabel="Cancel"
      >
        <input
          type="text"
          className="dialog-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Enter control unit name"
          autoFocus
        />
      </DialogBox>
    </div>
  );
};

export default ControlUnitStpDetails;
